/**
 * Client-side license gate.
 *
 * A build is "licensed" only when VITE_LICENSE_KEY is baked in. When it is
 * empty (stock / web deployments) every function here is a no-op: the key is
 * never appended to URLs and validateLicense() reports valid. That keeps
 * existing deployments behaving exactly as before — licensing is opt-in per
 * build, matched by the server's INSHARE_LICENSE_REQUIRED switch.
 */
import {backendRestUrl} from './backendUrl';

export interface LicenseResult {
    valid: boolean;
    reason?: string;
    expiresAt?: string;
    client?: string;
}

/** The license key baked into this build, or '' when unlicensed. */
export function getLicenseKey(): string {
    return (import.meta.env.VITE_LICENSE_KEY as string | undefined)?.trim() || '';
}

/** True when this build enforces a license. */
export function isLicensed(): boolean {
    return getLicenseKey() !== '';
}

/** Append ?license=<key> to a URL when this build carries a key. */
export function withLicense(url: string): string {
    const key = getLicenseKey();
    if (!key) {
        return url;
    }
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}license=${encodeURIComponent(key)}`;
}

/** Last validation result, cached so the UI can display expiry without re-fetching. */
let lastResult: LicenseResult | null = null;

/** The most recent validation result (null before the first validate call). */
export function getLicenseInfo(): LicenseResult | null {
    return lastResult;
}

/**
 * Ask the server whether this build's key is still valid. Unlicensed builds
 * short-circuit to valid. A network error is treated as valid (fail-open on
 * the client) so a transient outage doesn't lock users out — the server-side
 * gate on /stream remains the authoritative enforcement point.
 */
export async function validateLicense(): Promise<LicenseResult> {
    const key = getLicenseKey();
    if (!key) {
        return (lastResult = {valid: true});
    }
    try {
        const url = backendRestUrl('/api/license/validate') + '?license=' + encodeURIComponent(key);
        const res = await fetch(url);
        if (!res.ok) {
            return (lastResult = {valid: true});
        }
        return (lastResult = (await res.json()) as LicenseResult);
    } catch {
        return (lastResult = {valid: true});
    }
}

/** Format an ISO timestamp as e.g. "1 Jul 2027, 5:30 pm", or null if unusable. */
export function formatExpiry(iso?: string): string | null {
    if (!iso) {
        return null;
    }
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return null;
    }
    return d.toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

/**
 * A short human label about the license for display, or null when this build
 * is unlicensed (so nothing renders on stock builds). Examples:
 *   "License expires on 1 Jul 2027, 5:30 pm"
 *   "License revoked"
 */
export function licenseStatusLabel(): string | null {
    if (!isLicensed()) {
        return null;
    }
    const r = lastResult;
    if (!r) {
        return null;
    }
    if (!r.valid) {
        return `License ${r.reason || 'invalid'}`;
    }
    const when = formatExpiry(r.expiresAt);
    return when ? `License expires on ${when}` : 'Licensed';
}
